/* eslint-disable max-lines -- this file contains too many lines after rules update, this need to be refactored */
import { ICellRendererParams } from '@ag-grid-community/core';
import { NavigationExtras } from '@angular/router';

import { Order } from '../../../../order/models/order';
import { UserDatacenterView } from '../../../models/datacenter-aggrid-view';
import { TranslationService } from '../../../services/translation.service';
import {
	CommonColDef,
	DateColDef,
	IconLinkColDef,
	FavoriteOrdersColDef,
	PinOrdersColDef,
	DimensionColDef,
	LinkColDef,
	NumericColDef,
	MultiLineColDef,
	IconColDef
} from '../../column-defs';
import { ColumnType } from '../../column-type';
import { DatacenterDateFilterComponent } from '../../filter-type/datacenter-date-filter.component';
import { DatacenterNumberFilterComponent } from '../../filter-type/datacenter-number-filter.component';
import { DatacenterDimensionFilterComponent } from '../../filter-type/datacenter-dimension-filter.component';
import { DatacenterOrderNumberFilterComponent } from '../../filter-type/datacenter-order-number-filter.component';
import { DatacenterTextFilterComponent } from '../../filter-type/datacenter-text-filter.component';
import { MultiLineValueTypes } from '../../cell-renderers/multi-line-value-types';
import { DatacenterDropdownFilterComponent } from '../../filter-type/datacenter-dropdown-filter.component';
import { UserService } from '../../../services/user.service';

const documentsExtras: NavigationExtras = {
	fragment: 'documents',
};

const linkToOrderCardDocumentCellRendererParams = {
	routerLink(params: ICellRendererParams<Order, unknown>) {
		if (!params.data) {
			return null;
		}
		const order = params.data;
		return {
			commands: [
				'/order-tracking',
				'order',
				'card',
				order.supplierOrderNumber,
				order.supplierOrderItemNumber
			],
			extras: documentsExtras
		};
	}
};

const linkToOrderCardCellRendererParams = {
	routerLink(params: ICellRendererParams<Order, unknown>) {
		if (!params.data) {
			return null;
		}
		const { data } = params;
		return {
			commands: [
				'/order-tracking',
				'order',
				'card',
				data.supplierOrderNumber,
				data.supplierOrderItemNumber
			]
		};
	}
};

export class OrderDatacenterColumnDefinition {
	readonly ORDER_REQUIRED_FIELDS = ['supplierOrderNumber', 'supplierOrderItemNumber', 'orderItemStatus'];
	// eslint-disable-next-line @typescript-eslint/naming-convention
	ORDER_DATA_CENTER_DEFAULT_CONFIGURATION: Map<string, CommonColDef<Order> | CommonColDef<Order, string>>;

	// eslint-disable-next-line max-lines-per-function
	constructor(
		private readonly translationService: TranslationService,
		private readonly userService: UserService
	) {
		this.ORDER_DATA_CENTER_DEFAULT_CONFIGURATION =
		new Map<string, CommonColDef<Order> | CommonColDef<Order, string>>([
			[
				'favoriteOrders',
				new FavoriteOrdersColDef<Order>({
					headerName: '',
					colId: 'favoriteOrders',
				})
			],
			[
				'customerOrderNumber',
				new LinkColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.customer-order-number'),
					colId: 'customerOrderNumber',
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						if (!order.customerOrderNumber) {
							return '';
						}
						let value = order.customerOrderNumber;
						if (order.customerOrderItemNumber) {
							value += `/${ order.customerOrderItemNumber }`;
						}
						return value;
					},
					fields: ['customerOrderNumber', 'customerOrderItemNumber'],
					filter: DatacenterOrderNumberFilterComponent,
					suppressHeaderMenuButton: false,
					minWidth: 150,
					cellRendererParams: linkToOrderCardCellRendererParams,
					columnType: ColumnType.ORDER_NUMBER_COLUMN
				}),
			],
			[
				'supplierOrderNumber',
				new LinkColDef<Order>({
					headerName: this.translationService.getTR('order.supplier.order.number'),
					colId: 'supplierOrderNumber',
					valueGetter: params => this.getSupplierData(params.data),
					fields: ['supplierOrderNumber', 'supplierOrderItemNumber'],
					minWidth: 150,
					cellRendererParams: linkToOrderCardCellRendererParams,
					filter: DatacenterOrderNumberFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.ORDER_NUMBER_COLUMN
				})
			],
			[
				'supplierRFQNumber',
				new LinkColDef<Order>({
					headerName: this.translationService.getTR('application.procurement.supplier.rfqNumber'),
					colId: 'supplierRFQNumber',
					valueGetter: params => this.getSupplierRFQData(params.data),
					fields: ['supplierRFQNumber', 'supplierRFQItemNumber'],
					minWidth: 150,
					cellRendererParams: linkToOrderCardCellRendererParams,
					filter: DatacenterOrderNumberFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.ORDER_NUMBER_COLUMN
				})
			],
			[
				'steelgrade',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('order-tracking.steelgrade'),
					field: 'steelgradeDescription',
					colId: 'steelgrade',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'steelgradeStandard',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('order-tracking.standard-steel-grade'),
					field: 'steelgradeStandardDescription',
					colId: 'steelgradeStandard',
					filter: DatacenterDropdownFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					filterParams: { orderFields: ['steelgradeStandard', 'steelgradeStandardDescription'] },
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.steelgradeStandardDescription;
					},
				})
			],
			[
				'dimension',
				new DimensionColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.dimension'),
					dimensionGetter(params) {
						return params.data;
					},
					colId: 'dimension',
					fields: ['thickness', 'width', 'length'],
					filter: DatacenterDimensionFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.DIMENSION_COLUMN
				})
			],
			[
				'cddWeek',
				new DateColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.cdd'),
					colId: 'cddWeek',
					field: 'cddWeek',
					minWidth: 84,
					maxWidth: 84,
					width: 84,
					valueGetter: params => {
						if (!params.data?.cddWeek || params.data.cddWeek.includes('1900-W01')) {
							return '';
						}
						return params.data.cddWeek;
					},
					cellRendererParams: { inputFormat: 'isoWeek', outputFormat: 'week' },
					filter: DatacenterDateFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.DATE_COLUMN,
				})
			],
			[
				'rdd',
				new DateColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.rdd'),
					colId: 'rdd',
					field: 'rdd',
					minWidth: 120,
					maxWidth: 120,
					width: 120,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' },
					filter: DatacenterDateFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.DATE_COLUMN,
				})
			],
			[
				'rddWeek',
				new DateColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.rdd-week'),
					colId: 'rddWeek',
					field: 'rddWeek',
					minWidth: 84,
					maxWidth: 84,
					width: 84,
					cellRendererParams: { inputFormat: 'isoWeek', outputFormat: 'week' },
					filter: DatacenterDateFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.DATE_COLUMN
				})
			],
			[
				'mill',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.mill'),
					colId: 'mill',
					field: 'millDescription',
					filter: DatacenterTextFilterComponent,
					filterParams: { filterOptions: ['contains'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'qtyOrdered',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.ordered'),
					colId: 'qtyOrdered',
					field: 'grossQtyOrdered',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'qtyInProduction',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.qty-in-production'),
					colId: 'qtyInProduction',
					field: 'grossQtyInProduction',
					cellRendererParams: { numericFormat: 'weight-with-zero' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'qtyReadyToShip',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.rts-gross'),
					colId: 'qtyReadyToShip',
					field: 'grossQtyAvailableReadyToShip',
					cellRendererParams: { numericFormat: 'weight-with-zero' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'qtyInTransit',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.in-transit-gross'),
					colId: 'qtyInTransit',
					field: 'grossQtyAvailableInTransit',
					cellRendererParams: { numericFormat: 'weight-with-zero' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'availableDetailGross',
				new CommonColDef<Order>({
					headerName: 'Available - Detail (Gross, t)',
					colId: 'availableDetailGross',
					hide: true,
					sortable: false,
				})
			],
			[
				'grossQtyAvailableReadyToShip',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.rts-gross'),
					colId: 'grossQtyAvailableReadyToShip',
					field: 'grossQtyAvailableReadyToShip',
					groupId: 'availableDetailGross',
					cellRendererParams: { numericFormat: 'weight-with-zero' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'grossQtyAvailableInTransit',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.in-transit-gross'),
					colId: 'grossQtyAvailableInTransit',
					field: 'grossQtyAvailableInTransit',
					groupId: 'availableDetailGross',
					cellRendererParams: { numericFormat: 'weight-with-zero' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'grossQtyAvailableInConsignmentDetail',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.gross-consignment'),
					colId: 'grossQtyAvailableInConsignmentDetail',
					field: 'grossQtyAvailableInConsignment',
					groupId: 'availableDetailGross',
					cellRendererParams: { numericFormat: 'weight-with-zero' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'availableDetailNet',
				new CommonColDef<Order>({
					headerName: 'Available - Details (Net, t)',
					colId: 'availableDetailNet',
					hide: true,
					sortable: false,
				})
			],
			[
				'netQtyAvailableReadyToShipDetail',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.net-ready-to-ship'),
					colId: 'netQtyAvailableReadyToShipDetail',
					field: 'netQtyAvailableReadyToShip',
					groupId: 'availableDetailNet',
					cellRendererParams: { numericFormat: 'weight-with-zero' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'netQtyAvailableInTransitDetail',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.net-in-transit'),
					colId: 'netQtyAvailableInTransitDetail',
					field: 'netQtyAvailableInTransit',
					groupId: 'availableDetailNet',
					cellRendererParams: { numericFormat: 'weight-with-zero' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'netQtyAvailableInConsignmentDetail',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.net-consignment'),
					colId: 'netQtyAvailableInConsignmentDetail',
					field: 'netQtyAvailableInConsignment',
					groupId: 'availableDetailNet',
					cellRendererParams: { numericFormat: 'weight-with-zero' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'qtyNotDelivered',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.batches-not-delivered-gross'),
					colId: 'qtyNotDelivered',
					field: 'grossQtyNotDelivered',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'netQtyNotDelivered',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.batches-not-delivered-net'),
					colId: 'netQtyNotDelivered',
					field: 'netQtyNotDelivered',
					cellRendererParams: { numericFormat: 'weight-with-zero' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'qtyDelivered',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.qty-delivered'),
					colId: 'qtyDelivered',
					field: 'grossQtyDelivered',
					cellRendererParams: { numericFormat: 'weight-with-zero' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'netQtyDelivered',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.net-qty-delivered'),
					colId: 'netQtyDelivered',
					field: 'netQtyDelivered',
					cellRendererParams: { numericFormat: 'weight-with-zero' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'grossLastBatchInStock',
				new DateColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.gross-last-batch'),
					colId: 'grossLastBatchInStock',
					field: 'grossLastBatchInStock',
					minWidth: 84,
					maxWidth: 84,
					width: 84,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'week' },
					filter: DatacenterDateFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.DATE_COLUMN
				})
			],
			[
				'colorBottomSide',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.color-bottom-side'),
					field: 'colorBottomSide',
					colId: 'colorBottomSide',
					sortable: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					filter: DatacenterDropdownFilterComponent,
					filterParams: { orderFields: ['colorBottomSide', 'colorBottomSideDescription'] },
					suppressHeaderMenuButton: false,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.colorBottomSideDescription;
					},
				})
			],
			[
				'colorTopSide',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.color-top-side'),
					field: 'colorTopSide',
					colId: 'colorTopSide',
					columnType: ColumnType.TRANSLATED_COLUMN,
					filter: DatacenterDropdownFilterComponent,
					filterParams: { orderFields: ['colorTopSide', 'colorTopSideDescription'] },
					suppressHeaderMenuButton: false,
					sortable: false,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.colorTopSideDescription;
					},
				})
			],
			[
				'soldTo',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.sold-to'),
					field: 'soldToDescription',
					colId: 'soldTo',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'coating',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.coating'),
					field: 'coating',
					colId: 'coating',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { orderFields: ['coating', 'coatingDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: false,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.coatingDescription;
					}
				})
			],
			[
				'orderCreationDate',
				new DateColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.creation-date'),
					colId: 'orderCreationDate',
					field: 'orderCreationDate',
					minWidth: 120,
					maxWidth: 120,
					width: 120,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' },
					filter: DatacenterDateFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.DATE_COLUMN
				})
			],
			[
				'ebusinessOrderNumber',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.steeluser-order-number'),
					colId: 'ebusinessOrderNumber',
					valueGetter: params => {
						if (!params.data?.ebusinessOrderNumber) {
							return '';
						}
						let value = params.data.ebusinessOrderNumber;

						if (params.data.ebusinessOrderItemNumber) {
							value += `/${ params.data.ebusinessOrderItemNumber }`;
						}
						return value;
					},
					fields: ['ebusinessOrderNumber', 'ebusinessOrderItemNumber'],
					minWidth: 150,
					filter: DatacenterOrderNumberFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.ORDER_NUMBER_COLUMN
				})
			],
			[
				'customerArticleReference',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.customer-article-reference'),
					field: 'customerArticleReference',
					colId: 'customerArticleReference',
					minWidth: 100,
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'deliveryAddress',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.delivery-address'),
					field: 'deliveryAddress',
					colId: 'deliveryAddress',
					minWidth: 250,
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'endUser',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.end-user'),
					field: 'endUserDescription',
					colId: 'endUser',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'length',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('batch-card.length'),
					field: 'length',
					colId: 'length',
					filter: DatacenterNumberFilterComponent,
					cellRendererParams: { numericFormat: 'length-mm' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						if (!order.length) {
							return '';
						}
						return order.length;
					},
				})
			],
			[
				'thickness',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.thickness'),
					field: 'thickness',
					colId: 'thickness',
					filter: DatacenterNumberFilterComponent,
					cellRendererParams: { numericFormat: 'thickness' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'width',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.width'),
					field: 'width',
					colId: 'width',
					filter: DatacenterNumberFilterComponent,
					cellRendererParams: { numericFormat: 'width' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'millOrderNumber',
				new LinkColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.mill-order-number'),
					colId: 'millOrderNumber',
					valueGetter: params => this.getMillData(params.data),
					fields: ['millOrderNumber', 'millOrderItemNumber'],
					minWidth: 150,
					cellRendererParams: linkToOrderCardCellRendererParams,
					filter: DatacenterOrderNumberFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.ORDER_NUMBER_COLUMN
				})
			],
			[
				'orderItemStatus',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.order-item-status'),
					field: 'orderItemStatus',
					colId: 'orderItemStatus',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { orderFields: ['orderItemStatus', 'orderItemStatusDescription'] },
					suppressHeaderMenuButton: false,
					sortable: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.orderItemStatusDescription;
					}
				})
			],
			[
				'productCategory',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.product-category'),
					field: 'productCategory',
					colId: 'productCategory',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { orderFields: ['productCategory', 'productCategoryDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: false,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.productCategoryDescription;
					}
				})
			],
			[
				'productDescription',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.product-description'),
					field: 'productDescription',
					colId: 'productDescription',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					sortable: false,
					columnType: ColumnType.TEXT_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.productDescription;
					}
				})
			],
			[
				'salesQuarter',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.sales-quarter'),
					field: 'salesQuarter',
					colId: 'salesQuarter',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'shipTo',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.ship-to'),
					field: 'shipToDescription',
					colId: 'shipTo',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'surface',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.surface'),
					field: 'surface',
					colId: 'surface',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { orderFields: ['surface', 'surfaceDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: false,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.surfaceDescription;
					}
				})
			],
			[
				'transportMode',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.transport-mode'),
					field: 'transportMode',
					colId: 'transportMode',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { orderFields: ['transportMode', 'transportModeDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: false,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.transportModeDescription;
					}
				})
			],
			[
				'deliveryMode',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('filter.deliveryMode'),
					field: 'deliveryMode',
					colId: 'deliveryMode',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { orderFields: ['deliveryMode', 'deliveryModeDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: false,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.deliveryModeDescription;
					}
				})
			],
			[
				'netQtyAvailableReadyToShip',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.net-ready-to-ship'),
					colId: 'netQtyAvailableReadyToShip',
					field: 'netQtyAvailableReadyToShip',
					cellRendererParams: { numericFormat: 'weight-with-zero' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'netQtyAvailableInTransit',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.net-in-transit'),
					colId: 'netQtyAvailableInTransit',
					field: 'netQtyAvailableInTransit',
					cellRendererParams: { numericFormat: 'weight-with-zero' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'netQtyAvailableInConsignment',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.net-consignment'),
					colId: 'netQtyAvailableInConsignment',
					field: 'netQtyAvailableInConsignment',
					cellRendererParams: { numericFormat: 'weight-with-zero' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'grossQtyAvailableInConsignment',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.gross-consignment'),
					colId: 'grossQtyAvailableInConsignment',
					field: 'grossQtyAvailableInConsignment',
					cellRendererParams: { numericFormat: 'weight-with-zero' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'contractNumber',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('filter.contractNumber'),
					colId: 'contractNumber',
					field: 'supplierContractNumber',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'docLink',
				new IconLinkColDef<Order>({
					headerClass: ['copo-column-hide'],
					colId: 'docLink',
					headerName: this.translationService.getTR('datacenter.documents'),
					valueGetter: params => this.getSupplierData(params.data),
					cellRendererParams: {
						icon: 'table_document',
						...linkToOrderCardDocumentCellRendererParams
					},
					sortable: false,
				})
			],
			[
				'trademarkNameTopSide',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.trademark-name-top-side'),
					field: 'trademarkNameTopSide',
					colId: 'trademarkNameTopSide',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { orderFields: ['trademarkNameTopSide', 'trademarkNameTopSideDescription'] },
					suppressHeaderMenuButton: false,
					sortable: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.trademarkNameTopSideDescription;
					}
				})
			],
			[
				'trademarkNameBottomSide',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.trademark-name-bottom-side'),
					field: 'trademarkNameBottomSide',
					colId: 'trademarkNameBottomSide',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { orderFields: ['trademarkNameBottomSide', 'trademarkNameBottomSideDescription'] },
					suppressHeaderMenuButton: false,
					sortable: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.trademarkNameBottomSideDescription;
					}
				})
			],
			[
				'totalThicknessOrgCoatingTop',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.total-thickness-coating-top'),
					field: 'totalThicknessOrgCoatingTop',
					colId: 'totalThicknessOrgCoatingTop',
					filter: DatacenterNumberFilterComponent,
					cellRendererParams: { numericFormat: 'thickness' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'totalThicknessOrgCoatingBottom',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.total-thickness-coating-bottom'),
					field: 'totalThicknessOrgCoatingBottom',
					colId: 'totalThicknessOrgCoatingBottom',
					filter: DatacenterNumberFilterComponent,
					cellRendererParams: { numericFormat: 'thickness' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'nominalGlossTopSide',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.nominal-gloss-top-side'),
					field: 'nominalGlossTopSide',
					colId: 'nominalGlossTopSide',
					filter: DatacenterNumberFilterComponent,
					cellRendererParams: { numericFormat: 'thickness' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'nominalGlossBottomSide',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.nominal-gloss-bottom-side'),
					field: 'nominalGlossBottomSide',
					colId: 'nominalGlossBottomSide',
					filter: DatacenterNumberFilterComponent,
					cellRendererParams: { numericFormat: 'thickness' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'orderReason',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.order-reason'),
					field: 'orderReason',
					colId: 'orderReason',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { orderFields: ['orderReason', 'orderReasonDescription'] },
					suppressHeaderMenuButton: false,
					sortable: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.orderReasonDescription;
					}
				})
			],
			[
				'provisionalOrFirmItem',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.provisional-firm-item'),
					field: 'provisionalOrFirmItem',
					colId: 'provisionalOrFirmItem',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'serviceLevel',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.service-level'),
					field: 'serviceLevel',
					colId: 'serviceLevel',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { orderFields: ['serviceLevel', 'serviceLevelDescription'] },
					suppressHeaderMenuButton: false,
					sortable: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.serviceLevelDescription;
					}
				})
			],
			[
				'serviceRequest',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.service-request'),
					field: 'serviceRequest',
					colId: 'serviceRequest',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { orderFields: ['serviceRequest', 'serviceRequestDescription'] },
					suppressHeaderMenuButton: false,
					sortable: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.serviceRequestDescription;
					}
				})
			],
			[
				'dataSheetTopSide',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.data-sheet-top-side'),
					field: 'dataSheetTopSide',
					colId: 'dataSheetTopSide',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { orderFields: ['dataSheetTopSide', 'dataSheetTopSideDescription'] },
					suppressHeaderMenuButton: false,
					sortable: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.dataSheetTopSideDescription;
					}
				})
			],
			[
				'dataSheetBottomSide',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.data-sheet-bottom-side'),
					field: 'dataSheetBottomSide',
					colId: 'dataSheetBottomSide',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { orderFields: ['dataSheetBottomSide', 'dataSheetBottomSideDescription'] },
					suppressHeaderMenuButton: false,
					sortable: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.dataSheetBottomSideDescription;
					}
				})
			],
			[
				'organicCoatingTopSideStructure',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.organic-structure-top-side'),
					field: 'organicCoatingTopSideStructure',
					colId: 'organicCoatingTopSideStructure',
					filter: DatacenterDropdownFilterComponent,
					filterParams: {
						orderFields: ['organicCoatingTopSideStructure', 'organicCoatingTopSideStructureDescription']
					},
					sortable: false,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.organicCoatingTopSideStructureDescription;
					}
				})
			],
			[
				'organicCoatingBottomSideStructure',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.organic-structure-bottom-side'),
					field: 'organicCoatingBottomSideStructure',
					colId: 'organicCoatingBottomSideStructure',
					filter: DatacenterDropdownFilterComponent,
					filterParams: {
						orderFields: [
							'organicCoatingBottomSideStructure',
							'organicCoatingBottomSideStructureDescription'
						]
					},
					sortable: false,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.organicCoatingBottomSideStructureDescription;
					}
				})
			],
			[
				'xcarb',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('order.card.xcarb'),
					fields: ['xcarb', 'xcarbDescription'],
					colId: 'xcarb',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { orderFields: ['xcarb', 'xcarbDescription'] },
					sortable: false,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.xcarbDescription;
					}
				})
			],
			[
				'form',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.form'),
					field: 'form',
					colId: 'form',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { orderFields: ['form', 'formDescription'] },
					suppressHeaderMenuButton: false,
					sortable: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.formDescription;
					}
				})
			],
			[
				'wip',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('filter.workInProgress'),
					colId: 'wip',
					hide: true,
					sortable: false,
				})
			],
			[
				'qtyWipOpenNeeds',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.open-needs'),
					colId: 'qtyWipOpenNeeds',
					field: 'qtyWipOpenNeeds',
					groupId: 'wip',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'qtyWipInsufficientMaterialBalance',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.balanced-not-produced'),
					colId: 'qtyWipInsufficientMaterialBalance',
					field: 'qtyWipInsufficientMaterialBalance',
					groupId: 'wip',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'qtyWipHandedToPlanning',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.handed-over-to-planning'),
					colId: 'qtyWipHandedToPlanning',
					field: 'qtyWipHandedToPlanning',
					groupId: 'wip',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'qtyWipPlannedCaster',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.planned-casting'),
					colId: 'qtyWipPlannedCaster',
					field: 'qtyWipPlannedCaster',
					groupId: 'wip',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'qtyWipPrematerialOrdered',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.prematerial-ordered'),
					colId: 'qtyWipPrematerialOrdered',
					field: 'qtyWipPrematerialOrdered',
					groupId: 'wip',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'qtyWipSlab',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.slab'),
					colId: 'qtyWipSlab',
					field: 'qtyWipSlab',
					groupId: 'wip',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'qtyWipRollingStand',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.rolling-stand'),
					colId: 'qtyWipRollingStand',
					field: 'qtyWipRollingStand',
					groupId: 'wip',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'qtyWipHotRolled',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.hot-rolled'),
					colId: 'qtyWipHotRolled',
					field: 'qtyWipHotRolled',
					groupId: 'wip',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'qtyWipPickled',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.pickled'),
					colId: 'qtyWipPickled',
					field: 'qtyWipPickled',
					groupId: 'wip',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'qtyWipColdRolled',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.cold-rolled'),
					colId: 'qtyWipColdRolled',
					field: 'qtyWipColdRolled',
					groupId: 'wip',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'qtyWipAnnealed',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.annealed'),
					colId: 'qtyWipAnnealed',
					field: 'qtyWipAnnealed',
					groupId: 'wip',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'qtyWipNormalizing',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.normalizing'),
					colId: 'qtyWipNormalizing',
					field: 'qtyWipNormalizing',
					groupId: 'wip',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'qtyWipMetallicCoated',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.metallic-coated'),
					colId: 'qtyWipMetallicCoated',
					field: 'qtyWipMetallicCoated',
					groupId: 'wip',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'qtyWipOrganicCoated',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.organic-coated'),
					colId: 'qtyWipOrganicCoated',
					field: 'qtyWipOrganicCoated',
					groupId: 'wip',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'qtyWipSkinPass',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.skin-pass'),
					colId: 'qtyWipSkinPass',
					field: 'qtyWipSkinPass',
					groupId: 'wip',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'qtyWipOxiCutting',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.oxi-cutting'),
					colId: 'qtyWipOxiCutting',
					field: 'qtyWipOxiCutting',
					groupId: 'wip',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'qtyWipCuttingAndBeveling',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.cutting-beveling'),
					colId: 'qtyWipCuttingAndBeveling',
					field: 'qtyWipCuttingAndBeveling',
					groupId: 'wip',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'qtyWipShotBlasting',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.shot-blasting'),
					colId: 'qtyWipShotBlasting',
					field: 'qtyWipShotBlasting',
					groupId: 'wip',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'qtyWipSlitting',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.slitting'),
					colId: 'qtyWipSlitting',
					field: 'qtyWipSlitting',
					groupId: 'wip',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'qtyWipComposite',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.composite'),
					colId: 'qtyWipComposite',
					field: 'qtyWipComposite',
					groupId: 'wip',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'qtyWipQualityInspectionUnpacked',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.quality-control'),
					colId: 'qtyWipQualityInspectionUnpacked',
					field: 'qtyWipQualityInspectionUnpacked',
					groupId: 'wip',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'qtyWipMaterialOnPackingLine',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.packing'),
					colId: 'qtyWipMaterialOnPackingLine',
					field: 'qtyWipMaterialOnPackingLine',
					groupId: 'wip',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'inTransitGross',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.gross-in-transit-details'),
					colId: 'inTransitGross',
					hide: true,
					sortable: false,
				})
			],
			[
				'grossQtyLandTransit',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.land-transit-gross'),
					colId: 'grossQtyLandTransit',
					field: 'grossQtyLandTransit',
					groupId: 'inTransitGross',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'grossQtyAtReshipper',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.at-reshipper-gross'),
					colId: 'grossQtyAtReshipper',
					field: 'grossQtyAtReshipper',
					groupId: 'inTransitGross',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'grossQtyOnVessel',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.on-vessel-gross'),
					colId: 'grossQtyOnVessel',
					field: 'grossQtyOnVessel',
					groupId: 'inTransitGross',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'grossQtyOverseasTransit',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.overseas-transit-gross'),
					colId: 'grossQtyOverseasTransit',
					field: 'grossQtyOverseasTransit',
					groupId: 'inTransitGross',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'grossQtyInTransitToCustomer',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.in-transit-to-customer-gross'),
					colId: 'grossQtyInTransitToCustomer',
					field: 'grossQtyInTransitToCustomer',
					groupId: 'inTransitGross',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'inTransitNet',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.net-in-transit-details'),
					colId: 'inTransitNet',
					hide: true,
					sortable: false,
				})
			],
			[
				'netQtyLandTransit',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.land-transit-net'),
					colId: 'netQtyLandTransit',
					field: 'netQtyLandTransit',
					groupId: 'inTransitNet',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'netQtyAtReshipper',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.at-reshipper-net'),
					colId: 'netQtyAtReshipper',
					field: 'netQtyAtReshipper',
					groupId: 'inTransitNet',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'netQtyOnVessel',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.on-vessel-net'),
					colId: 'netQtyOnVessel',
					field: 'netQtyOnVessel',
					groupId: 'inTransitNet',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'netQtyOverseasTransit',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.overseas-transit-net'),
					colId: 'netQtyOverseasTransit',
					field: 'netQtyOverseasTransit',
					groupId: 'inTransitNet',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'netQtyInTransitToCustomer',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.in-transit-to-customer-net'),
					colId: 'netQtyInTransitToCustomer',
					field: 'netQtyInTransitToCustomer',
					groupId: 'inTransitNet',
					cellRendererParams: { numericFormat: 'weight' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'orderManager',
				new CommonColDef({
					headerName: this.translationService.getTR('order.responsible.order.manager'),
					field: 'orderManagerDescription',
					colId: 'orderManager',
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					filter: DatacenterDropdownFilterComponent,
					filterParams: { orderFields: ['orderManager', 'orderManagerDescription'] },
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.orderManagerDescription;
					}
				})
			],
			[
				'itemReferenceSoldToParty',
				new CommonColDef({
					headerName: this.translationService.getTR('datacenter-filter.item-sold-to-party'),
					field: 'itemReferenceSoldToParty',
					colId: 'itemReferenceSoldToParty',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'billTo',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.bill-to'),
					field: 'billToDescription',
					colId: 'billTo',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { orderFields: ['billTo', 'billToDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.billToDescription;
					}
				})
			],
			[
				'chemicalPassivation',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.chemical-passivation'),
					field: 'chemicalPassivationDescription',
					colId: 'chemicalPassivation',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { orderFields: ['chemicalPassivation', 'chemicalPassivationDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.chemicalPassivationDescription;
					}
				})
			],
			[
				'certificate',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('order.certificate'),
					field: 'certificateDescription',
					colId: 'certificate',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { orderFields: ['certificate', 'certificateDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.certificateDescription;
					}
				})
			],
			[
				'colorCategoryBottom',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.color-category-bottom-side'),
					field: 'colorCategoryBottomDescription',
					colId: 'colorCategoryBottom',
					suppressHeaderMenuButton: false,
					filter: DatacenterDropdownFilterComponent,
					filterParams: { orderFields: ['colorCategoryBottom', 'colorCategoryBottomDescription'] },
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.colorCategoryBottomDescription;
					}
				})
			],
			[
				'colorCategoryTop',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.color-category-top-side'),
					field: 'colorCategoryTopDescription',
					colId: 'colorCategoryTop',
					suppressHeaderMenuButton: false,
					filter: DatacenterDropdownFilterComponent,
					filterParams: { orderFields: ['colorCategoryTop', 'colorCategoryTopDescription'] },
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.colorCategoryTopDescription;
					}
				})
			],
			[
				'brandNamePaintTypeBottomSide',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.easy-film-bottom'),
					field: 'brandNamePaintTypeBottomSideDescription',
					colId: 'brandNamePaintTypeBottomSide',
					filter: DatacenterDropdownFilterComponent,
					suppressHeaderMenuButton: false,
					filterParams: {
						orderFields: [
							'brandNamePaintTypeBottomSide',
							'brandNamePaintTypeBottomSideDescription'
						]
					},
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.brandNamePaintTypeBottomSideDescription;
					}
				})
			],
			[
				'brandNamePaintTypeTopSide',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.easy-film-top'),
					field: 'brandNamePaintTypeTopSideDescription',
					colId: 'brandNamePaintTypeTopSide',
					filter: DatacenterDropdownFilterComponent,
					suppressHeaderMenuButton: false,
					filterParams: {
						orderFields: [
							'brandNamePaintTypeTopSide',
							'brandNamePaintTypeTopSideDescription'
						]
					},
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.brandNamePaintTypeTopSideDescription;
					}
				})
			],
			[
				'insideDiameterInmm',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.internal-diameter'),
					field: 'insideDiameterInmm',
					colId: 'insideDiameterInmm',
					filter: DatacenterNumberFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'kindOfOil',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.oil-reference'),
					field: 'kindOfOilDescription',
					colId: 'kindOfOil',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { orderFields: ['kindOfOil', 'kindOfOilDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.kindOfOilDescription;
					}
				})
			],
			[
				'colorFirstStrokeCross',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.paint-marking-for-sh'),
					field: 'colorFirstStrokeCrossDescription',
					colId: 'colorFirstStrokeCross',
					filter: DatacenterDropdownFilterComponent,
					suppressHeaderMenuButton: false,
					filterParams: {
						removeNoValueFilter: true,
						orderFields: [
							'colorFirstStrokeCross',
							'colorFirstStrokeCrossDescription'
						]
					},
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.colorFirstStrokeCrossDescription;
					}
				})
			],
			[
				'pattern',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.pattern-for-sh'),
					field: 'patternDescription',
					colId: 'pattern',
					filter: DatacenterDropdownFilterComponent,
					suppressHeaderMenuButton: false,
					filterParams: {
						orderFields: [
							'pattern',
							'patternDescription'
						]
					},
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.patternDescription;
					}
				})
			],
			[
				'materialNumber',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('order.sub.category'),
					field: 'materialNumber',
					colId: 'materialNumber',
					filter: DatacenterDropdownFilterComponent,
					suppressHeaderMenuButton: false,
					filterParams: {
						orderFields: [
							'materialNumber',
							'materialNumber'
						]
					},
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.materialNumber;
					}
				})
			],
			[
				'ultrasonicTesting',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.ultrasonic-testing'),
					field: 'ultrasonicTestingDescription',
					colId: 'ultrasonicTesting',
					filter: DatacenterDropdownFilterComponent,
					suppressHeaderMenuButton: false,
					filterParams: {
						removeNoValueFilter: true,
						orderFields: [
							'ultrasonicTesting',
							'ultrasonicTestingDescription'
						]
					},
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.ultrasonicTestingDescription;
					}
				})
			],
			[
				'additionalTests',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.z-test-certificat'),
					field: 'additionalTests',
					colId: 'additionalTests',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'choice',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('order.choice'),
					field: 'choice',
					colId: 'choice',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { orderFields: ['choice', 'choiceDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.choiceDescription;
					}
				})
			],
			[
				'customerOrderNumberWithoutItem',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.customer-order-number-without-item'),
					field: 'customerOrderNumber',
					colId: 'customerOrderNumberWithoutItem',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'finalUse',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.final-use'),
					field: 'finalUse',
					colId: 'finalUse',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { orderFields: ['finalUse', 'finalUseDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.finalUseDescription;
					}
				})
			],
			[
				'grossFirstBatchInStock',
				new DateColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.gross-first-batch'),
					colId: 'grossFirstBatchInStock',
					field: 'grossFirstBatchInStock',
					minWidth: 84,
					maxWidth: 84,
					width: 84,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'week' },
					filter: DatacenterDateFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.DATE_COLUMN,
				})
			],
			[
				'incoterm',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('order.incoterm'),
					field: 'incoterm',
					colId: 'incoterm',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { orderFields: ['incoterm', 'incotermDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.incotermDescription;
					}
				})
			],
			[
				'incoterm2',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.delivery-location'),
					field: 'incoterm2',
					colId: 'incoterm2',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { orderFields: ['incoterm2', 'incoterm2'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.incoterm2;
					}
				})
			],
			[
				'interpretationDeliveryDate',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.interpretation-delivery-date'),
					field: 'interpretationDeliveryDate',
					colId: 'interpretationDeliveryDate',
					filter: DatacenterDropdownFilterComponent,
					filterParams: {
						orderFields: ['interpretationDeliveryDate', 'interpretationDeliveryDateDescription']
					},
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.interpretationDeliveryDateDescription;
					}
				})
			],
			[
				'destinationByBoat',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.destination-by-boat'),
					field: 'destinationByBoat',
					colId: 'destinationByBoat',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'previsionItem',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('order.prevision.item'),
					field: 'previsionItem',
					colId: 'previsionItem',
					filter: DatacenterDropdownFilterComponent,
					filterParams: {
						removeNoValueFilter: true,
						orderFields: ['previsionItem', 'previsionItemDescription']
					},
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.previsionItemDescription;
					}
				})
			],
			[
				'protection',
				new CommonColDef<Order>({
					headerName: this.translationService.getTR('datacenter-filter.protection'),
					field: 'protectionDescription',
					colId: 'protection',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { orderFields: ['protection', 'protectionDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const order = params.data;
						return order.protectionDescription;
					}
				})
			],
			[
				'stockQtyBlockedGross',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.stock-blocked-gross'),
					colId: 'stockQtyBlockedGross',
					field: 'grossQtyBlocked',
					cellRendererParams: { numericFormat: 'weight-with-zero' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'stockQtyBlockedNet',
				new NumericColDef<Order>({
					headerName: this.translationService.getTR('datacenter.stock-blocked-net'),
					colId: 'stockQtyBlockedNet',
					field: 'netQtyBlocked',
					cellRendererParams: { numericFormat: 'weight-with-zero' },
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'stockAlert',
				new IconColDef<Order>({
					headerName: this.translationService.getTR('order.stock-alert'),
					colId: 'stockAlert',
					field: 'stockAlert',
					minWidth: 50,
					maxWidth: 50,
					width: 50,
					filterParams: {	removeNoValueFilter: true },
					suppressHeaderMenuButton: false,
					filter: DatacenterDropdownFilterComponent,
					columnType: ColumnType.COLOUR_COLUMN,
					cellRendererParams: {
						icon: (params: ICellRendererParams<Order>) => {
							const order = params.data;
							if (!order) {
								return '';
							}
							switch (order.stockAlert) {
							case '1':
								return {
									iconName: 'in_stock',
									toolTipText: this.translationService.getTR('stock-alert.green')
								};
							case '2':
								return {
									iconName: 'in_stock_orange',
									toolTipText: this.translationService.getTR('stock-alert.orange')
								};
							case '3':
								return {
									iconName: 'in_stock_red',
									toolTipText: this.translationService.getTR('stock-alert.red')
								};
							default:
								return { iconName: '', displayEmpty: true };
							}
						},
						onClick() {
							// noop
						},
					},
					sortable: false,
				})
			]
		]);
	}

	buildYearWeek(yearWeek: string) {
		const year = yearWeek.slice(0, yearWeek.indexOf('W'));
		const week = yearWeek.slice(yearWeek.indexOf('W') + 1);

		return `Week ${ week } ${ year }`;
	}

	addExceptionReportFields() {
		const hasAccess = this.userService.user.hasServiceParameterEqualsTo('PORDT', 'ExceptionReport', 'True');
		if (hasAccess) {
			this.ORDER_DATA_CENTER_DEFAULT_CONFIGURATION.set(
				'pinOrders',
				new PinOrdersColDef<Order>({
					headerName: '',
					colId: 'pinOrders',
				})
			);
			this.ORDER_DATA_CENTER_DEFAULT_CONFIGURATION.set(
				'comments',
				new MultiLineColDef<Order>({
					autoHeight: true,
					headerName: this.translationService.getTR('exception-report.comment-by-customer'),
					valueGetter: params => {
						const order = params.data;
						return order?.comments
							? order.comments.map(item => `${ this.buildYearWeek(item.yearWeek) } : ${ item.comment }`)
								.join(';') : '';
					},
					field: 'comments',
					colId: 'comments',
					minWidth: 150,
					suppressHeaderMenuButton: true,
					columnType: ColumnType.TEXT_COLUMN,
					sortable: false,
					valueType: MultiLineValueTypes.TEXT
				})
			);
			this.ORDER_DATA_CENTER_DEFAULT_CONFIGURATION.set(
				'answers',
				new MultiLineColDef<Order>({
					autoHeight: true,
					headerName: this.translationService.getTR('exception-report.comment-by-am'),
					valueGetter: params => {
						const order = params.data;
						return order?.answers
							? order.answers.map(item => `${ this.buildYearWeek(item.yearWeek) } : ${ item.comment }`)
								.join(';') : '';
					},
					field: 'answers',
					colId: 'answers',
					minWidth: 150,
					suppressHeaderMenuButton: true,
					columnType: ColumnType.TEXT_COLUMN,
					sortable: false,
					valueType: MultiLineValueTypes.TEXT
				})
			);
			this.ORDER_DATA_CENTER_DEFAULT_CONFIGURATION.set(
				'recentComments',
				new MultiLineColDef<Order>({
					autoHeight: true,
					headerName: this.translationService.getTR('exception-report.last.comment-by-customer'),
					valueGetter: params => {
						const order = params.data;
						return order?.recentComments
							? order.recentComments.map(item => `${ this.buildYearWeek(item.yearWeek) }
								: ${ item.comment }`)
								.join(';') : '';
					},
					field: 'recentComments',
					colId: 'recentComments',
					minWidth: 150,
					suppressHeaderMenuButton: true,
					columnType: ColumnType.TEXT_COLUMN,
					sortable: false,
					valueType: MultiLineValueTypes.TEXT
				})
			);
			this.ORDER_DATA_CENTER_DEFAULT_CONFIGURATION.set(
				'recentAnswers',
				new MultiLineColDef<Order>({
					autoHeight: true,
					headerName: this.translationService.getTR('exception-report.last.comment-by-am'),
					valueGetter: params => {
						const order = params.data;
						return order?.recentAnswers
							? order.recentAnswers.map(item => `${ this.buildYearWeek(item.yearWeek) }
								: ${ item.comment }`)
								.join(';') : '';
					},
					field: 'recentAnswers',
					colId: 'recentAnswers',
					minWidth: 150,
					suppressHeaderMenuButton: true,
					columnType: ColumnType.TEXT_COLUMN,
					sortable: false,
					valueType: MultiLineValueTypes.TEXT
				})
			);
		}
	}

	private getSupplierData(order?: Order) {
		if (!order?.supplierOrderNumber) {
			return '';
		}
		let value = order.supplierOrderNumber;

		if (order.supplierOrderItemNumber) {
			value += `/${ order.supplierOrderItemNumber }`;
		}
		return value;
	}

	private getSupplierRFQData(order?: Order) {
		if (!order?.supplierRFQNumber) {
			return '';
		}
		let value = order.supplierRFQNumber;

		if (order.supplierRFQItemNumber) {
			value += `/${ order.supplierRFQItemNumber }`;
		}
		return value;
	}

	private getMillData(order?: Order) {
		if (!order?.millOrderNumber) {
			return '';
		}
		let value = order.millOrderNumber;

		if (order.millOrderItemNumber) {
			value += `/${ order.millOrderItemNumber }`;
		}
		return value;
	}
}

export const DEFAULT_VIEW_ORDER_DATACENTER: UserDatacenterView = {
	columnState: [
		{
			colId: 'customerOrderNumber',
			pinned: 'left'
		},
		{
			colId: 'supplierOrderNumber',
			pinned: 'left'
		},
		{
			colId: 'steelgrade',
			pinned: 'left'
		},
		{
			colId: 'dimension',
			pinned: 'left'
		},
		{
			colId: 'cddWeek',
			pinned: 'left'
		},
		{
			colId: 'mill'
		},
		{
			colId: 'qtyOrdered'
		},
		{
			colId: 'qtyInProduction'
		},
		{
			colId: 'qtyReadyToShip'
		},
		{
			colId: 'qtyInTransit'
		},
		{
			colId: 'qtyNotDelivered'
		},
		{
			colId: 'qtyDelivered'
		},
		{
			colId: 'grossLastBatchInStock'
		},
		{
			colId: 'docLink'
		}
	],
	sortModel: [
		{
			colId: 'cddWeek',
			sort: 'asc'
		}
	],
	filterState: [],
	type: 'order',
	isDefault: true,
	name: 'Default',
	id: -99
};
